import React, { useEffect, useState } from "react";
import { getcashflow } from "../../../../services/admin";
import moment from "moment";
import baseUrl from "../../../../Api";
import { printReportUrl } from "../../../../utils/printPdf";

export default function CashFlow() {
    const [startDate, setStartDate] = useState(moment().startOf('year').format("YYYY-MM-DD"));
    const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchReport();
    }, []);

    const fetchReport = () => {
        setLoading(true);
        const data = {
            start_date: startDate,
            end_date: endDate,
            branch_id: localStorage.getItem("branch"),
        };

        getcashflow(data).then((res) => {
            if (res.status === true) {
                setReportData(res.data);
            }
            setLoading(false);
        });
    };

    const handleGenerate = () => {
        fetchReport();
    };

    const handleDownloadPDF = () => {
        const token = localStorage.getItem("token");
        const branchId = localStorage.getItem("branch");

        const url = `${baseUrl}/cashflow/pdf?start_date=${startDate}&end_date=${endDate}&branch_id=${branchId}`;

        fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
            }
        })
        .then(response => response.blob())
        .then(blob => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `CashFlow_${moment(startDate).format('DD-MM-YYYY')}_to_${moment(endDate).format('DD-MM-YYYY')}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        })
        .catch(error => {
            console.error('Error downloading PDF:', error);
            alert('Error downloading PDF');
        });
    };

    // Print sends the same PDF the download button produces straight to the printer.
    const handlePrint = () => {
        const token = localStorage.getItem("token");
        const branchId = localStorage.getItem("branch");

        printReportUrl(`${baseUrl}/cashflow/pdf?start_date=${startDate}&end_date=${endDate}&branch_id=${branchId}&output=html`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
            }
        });
    };

    const handleDownloadExcel = () => {
        const token = localStorage.getItem("token");
        const branchId = localStorage.getItem("branch");

        const url = `${baseUrl}/cashflow/excel?start_date=${startDate}&end_date=${endDate}&branch_id=${branchId}`;

        fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
            }
        })
        .then(response => response.blob())
        .then(blob => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `CashFlow_${moment(startDate).format('DD-MM-YYYY')}_to_${moment(endDate).format('DD-MM-YYYY')}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        })
        .catch(error => {
            console.error('Error downloading Excel:', error);
            alert('Error downloading Excel');
        });
    };

    const formatCurrency = (amount) => {
        return parseFloat(amount || 0).toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    return (
        <div>
            <div className="page-header">
                <h2 className="main-content-title tx-24 mg-b-5">Cash Flow Statement</h2>
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <a>Accounts</a>
                    </li>
                    <li className="breadcrumb-item">
                        <a>Reports</a>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                        Cash Flow
                    </li>
                </ol>
            </div>

            <div className="content-body">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-header">
                                    <div className="row">
                                        <div className="col-md-6">
                                            <h4 className="card-title">Cash Flow Statement</h4>
                                        </div>
                                        <div className="col-md-6 text-end">
                                            {reportData && (
                                                <>
                                                    <button className="btn btn-danger me-2" onClick={handleDownloadPDF}>
                                                        <i className="fa fa-file-pdf"></i> Download PDF
                                                    </button>
                                                    <button className="btn btn-success me-2" onClick={handleDownloadExcel}>
                                                        <i className="fa fa-file-excel"></i> Download Excel
                                                    </button>
                                                    <button className="btn btn-primary" onClick={handlePrint}>
                                                        <i className="fa fa-print"></i> Print
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="card-body">
                            <div className="row mb-4">
                                <div className="col-md-3">
                                    <label className="form-label">From Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">To Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                    />
                                </div>
                                <div className="col-md-2 d-flex align-items-end">
                                    <button
                                        className="btn btn-primary"
                                        onClick={handleGenerate}
                                        disabled={loading}
                                    >
                                        <i className="fa fa-search"></i> {loading ? 'Loading...' : 'Generate'}
                                    </button>
                                </div>
                            </div>

                            {reportData && (
                                <>
                                    <div className="text-center mb-4">
                                        <h5>Cash Flow Statement</h5>
                                        <p className="text-muted">
                                            For the period from {moment(reportData.period?.start_date || startDate).format('DD-MM-YYYY')}
                                            {' '}to {moment(reportData.period?.end_date || endDate).format('DD-MM-YYYY')}
                                        </p>
                                    </div>

                                    <div className="table-responsive">
                                        <table className="table table-bordered">
                                            <thead className="table-light">
                                                <tr>
                                                    <th width="60%">Particulars</th>
                                                    <th width="20%" className="text-end">Amount (₹)</th>
                                                    <th width="20%" className="text-end">Net Cash (₹)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {/* Opening Balance */}
                                                <tr className="table-info">
                                                    <td><strong>Opening Cash & Cash Equivalents</strong></td>
                                                    <td></td>
                                                    <td className="text-end">
                                                        <strong>{formatCurrency(reportData.opening_cash_balance || 0)}</strong>
                                                    </td>
                                                </tr>

                                                {/* Operating Activities */}
                                                <tr className="table-primary">
                                                    <td colSpan="3"><strong>A. CASH FLOW FROM OPERATING ACTIVITIES</strong></td>
                                                </tr>

                                                <tr>
                                                    <td className="ps-3"><em>Cash Inflows:</em></td>
                                                    <td></td>
                                                    <td></td>
                                                </tr>

                                                {(reportData.operating_activities?.inflows || []).map((item, idx) => (
                                                    <tr key={`op-inflow-${idx}`}>
                                                        <td className="ps-5">{item.account_name}</td>
                                                        <td className="text-end text-success">{formatCurrency(item.amount)}</td>
                                                        <td></td>
                                                    </tr>
                                                ))}

                                                <tr>
                                                    <td className="ps-4"><strong>Total Inflows</strong></td>
                                                    <td className="text-end text-success">
                                                        <strong>{formatCurrency(reportData.operating_activities?.total_inflows || 0)}</strong>
                                                    </td>
                                                    <td></td>
                                                </tr>

                                                <tr>
                                                    <td className="ps-3"><em>Cash Outflows:</em></td>
                                                    <td></td>
                                                    <td></td>
                                                </tr>

                                                {(reportData.operating_activities?.outflows || []).map((item, idx) => (
                                                    <tr key={`op-outflow-${idx}`}>
                                                        <td className="ps-5">{item.account_name}</td>
                                                        <td className="text-end text-danger">({formatCurrency(item.amount)})</td>
                                                        <td></td>
                                                    </tr>
                                                ))}

                                                <tr>
                                                    <td className="ps-4"><strong>Total Outflows</strong></td>
                                                    <td className="text-end text-danger">
                                                        <strong>({formatCurrency(reportData.operating_activities?.total_outflows || 0)})</strong>
                                                    </td>
                                                    <td></td>
                                                </tr>

                                                <tr className="table-secondary">
                                                    <td><strong>Net Cash from Operating Activities</strong></td>
                                                    <td></td>
                                                    <td className={`text-end ${(reportData.operating_activities?.net || 0) >= 0 ? 'text-success' : 'text-danger'}`}>
                                                        <strong>
                                                            {(reportData.operating_activities?.net || 0) >= 0 ? '' : '('}
                                                            {formatCurrency(Math.abs(reportData.operating_activities?.net || 0))}
                                                            {(reportData.operating_activities?.net || 0) >= 0 ? '' : ')'}
                                                        </strong>
                                                    </td>
                                                </tr>

                                                {/* Investing Activities */}
                                                <tr className="table-primary">
                                                    <td colSpan="3"><strong>B. CASH FLOW FROM INVESTING ACTIVITIES</strong></td>
                                                </tr>

                                                {(reportData.investing_activities?.activities || []).length > 0 ? (
                                                    (reportData.investing_activities?.activities || []).map((item, idx) => (
                                                        <tr key={`inv-${idx}`}>
                                                            <td className="ps-4">{item.account_name}</td>
                                                            <td className={`text-end ${item.type === 'inflow' ? 'text-success' : 'text-danger'}`}>
                                                                {item.type === 'outflow' ? '(' : ''}
                                                                {formatCurrency(Math.abs(item.amount))}
                                                                {item.type === 'outflow' ? ')' : ''}
                                                            </td>
                                                            <td></td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td className="ps-4 text-muted" colSpan="3">No investing activities</td>
                                                    </tr>
                                                )}

                                                <tr className="table-secondary">
                                                    <td><strong>Net Cash from Investing Activities</strong></td>
                                                    <td></td>
                                                    <td className={`text-end ${(reportData.investing_activities?.net || 0) >= 0 ? 'text-success' : 'text-danger'}`}>
                                                        <strong>
                                                            {(reportData.investing_activities?.net || 0) >= 0 ? '' : '('}
                                                            {formatCurrency(Math.abs(reportData.investing_activities?.net || 0))}
                                                            {(reportData.investing_activities?.net || 0) >= 0 ? '' : ')'}
                                                        </strong>
                                                    </td>
                                                </tr>

                                                {/* Financing Activities */}
                                                <tr className="table-primary">
                                                    <td colSpan="3"><strong>C. CASH FLOW FROM FINANCING ACTIVITIES</strong></td>
                                                </tr>

                                                {(reportData.financing_activities?.activities || []).length > 0 ? (
                                                    (reportData.financing_activities?.activities || []).map((item, idx) => (
                                                        <tr key={`fin-${idx}`}>
                                                            <td className="ps-4">{item.account_name}</td>
                                                            <td className={`text-end ${item.type === 'inflow' ? 'text-success' : 'text-danger'}`}>
                                                                {item.type === 'outflow' ? '(' : ''}
                                                                {formatCurrency(item.amount)}
                                                                {item.type === 'outflow' ? ')' : ''}
                                                            </td>
                                                            <td></td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td className="ps-4 text-muted" colSpan="3">No financing activities</td>
                                                    </tr>
                                                )}

                                                <tr className="table-secondary">
                                                    <td><strong>Net Cash from Financing Activities</strong></td>
                                                    <td></td>
                                                    <td className={`text-end ${(reportData.financing_activities?.net || 0) >= 0 ? 'text-success' : 'text-danger'}`}>
                                                        <strong>
                                                            {(reportData.financing_activities?.net || 0) >= 0 ? '' : '('}
                                                            {formatCurrency(Math.abs(reportData.financing_activities?.net || 0))}
                                                            {(reportData.financing_activities?.net || 0) >= 0 ? '' : ')'}
                                                        </strong>
                                                    </td>
                                                </tr>

                                                {/* Net Cash Flow */}
                                                <tr className="table-warning">
                                                    <td><strong>NET INCREASE/(DECREASE) IN CASH</strong></td>
                                                    <td></td>
                                                    <td className={`text-end ${(reportData.net_cash_flow || 0) >= 0 ? 'text-success' : 'text-danger'}`}>
                                                        <strong>
                                                            {(reportData.net_cash_flow || 0) >= 0 ? '' : '('}
                                                            {formatCurrency(Math.abs(reportData.net_cash_flow || 0))}
                                                            {(reportData.net_cash_flow || 0) >= 0 ? '' : ')'}
                                                        </strong>
                                                    </td>
                                                </tr>

                                                {/* Closing Balance */}
                                                <tr className="table-success">
                                                    <td><strong>CLOSING CASH & CASH EQUIVALENTS</strong></td>
                                                    <td></td>
                                                    <td className="text-end">
                                                        <strong>{formatCurrency(reportData.closing_cash_balance || 0)}</strong>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    {reportData.verification?.is_balanced && (
                                        <div className="alert alert-success mt-3">
                                            <i className="fa fa-check-circle"></i> Cash flow reconciliation verified!
                                            Opening Balance ({formatCurrency(reportData.opening_cash_balance || 0)}) + Net Cash Flow ({formatCurrency(reportData.net_cash_flow || 0)}) = Closing Balance ({formatCurrency(reportData.closing_cash_balance || 0)})
                                        </div>
                                    )}

                                    {reportData.verification && !reportData.verification.is_balanced && (
                                        <div className="alert alert-warning mt-3">
                                            <i className="fa fa-exclamation-triangle"></i> Minor reconciliation difference detected.
                                        </div>
                                    )}
                                </>
                            )}

                            {!reportData && !loading && (
                                <div className="text-center py-5">
                                    <p className="text-muted">Click "Generate" to view the Cash Flow Statement</p>
                                </div>
                            )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
